// src/utils/reportGenerator.js
// Generate monthly PDF report using jsPDF + jspdf-autotable
import { jsPDF } from 'jspdf';
import 'jspdf-autotable';

export function generateMonthlyReport({ stats, settings, transactions, goals, netWorth, month }) {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();

    // Header
    doc.setFillColor(26, 26, 46); // dark background
    doc.rect(0, 0, pageWidth, 40, 'F');
    doc.setTextColor(168, 85, 247); // accent purple
    doc.setFontSize(22);
    doc.text('Strategic Finance Report', 14, 20);
    doc.setTextColor(148, 163, 184); // frost
    doc.setFontSize(10);
    doc.text(`Month: ${month || 'Current'}`, 14, 30);
    doc.text(`Generated: ${new Date().toLocaleDateString('en-IN')}`, pageWidth - 14, 30, { align: 'right' });

    let y = 50;

    // Summary Section
    doc.setTextColor(30, 30, 60);
    doc.setFontSize(14);
    doc.text('Financial Summary', 14, y);
    y += 10;

    const summaryData = [
        ['Total Income', `₹${(stats?.income || 0).toLocaleString()}`],
        ['Total Expenses', `₹${(stats?.expenses || 0).toLocaleString()}`],
        ['Net Savings', `₹${(stats?.profit || 0).toLocaleString()}`],
        ['Savings Rate', `${(stats?.savingsRate || 0).toFixed(1)}%`],
        ['Transaction Count', `${stats?.transactionCount || 0}`],
        ['Daily Average Spend', `₹${(stats?.expenses > 0 ? (stats.expenses / new Date().getDate()).toFixed(0) : 0)}`],
    ];

    if (netWorth !== undefined) {
        summaryData.push(['Net Worth', `₹${netWorth.toLocaleString()}`]);
    }

    doc.autoTable({
        startY: y,
        head: [['Metric', 'Value']],
        body: summaryData,
        theme: 'striped',
        headStyles: { fillColor: [168, 85, 247] },
        margin: { left: 14, right: 14 },
    });

    y = doc.lastAutoTable.finalY + 15;

    // Category Breakdown
    const categories = Object.entries(stats?.categoryBreakdown || {}).sort((a, b) => b[1] - a[1]);
    if (categories.length > 0) {
        doc.setFontSize(14);
        doc.text('Category Breakdown', 14, y);
        y += 5;

        doc.autoTable({
            startY: y,
            head: [['Category', 'Amount', '% of Total']],
            body: categories.map(([cat, amt]) => [
                cat,
                `₹${amt.toLocaleString()}`,
                `${((amt / (stats?.expenses || 1)) * 100).toFixed(1)}%`,
            ]),
            theme: 'striped',
            headStyles: { fillColor: [168, 85, 247] },
            margin: { left: 14, right: 14 },
        });

        y = doc.lastAutoTable.finalY + 15;
    }

    // Top 10 Expenses
    const topExpenses = (transactions || [])
        .filter(t => t.type === 'debit' || t.type === 'expense')
        .sort((a, b) => Math.abs(b.amount) - Math.abs(a.amount))
        .slice(0, 10);

    if (topExpenses.length > 0) {
        if (y > 240) { doc.addPage(); y = 20; }
        doc.setFontSize(14);
        doc.text('Top Expenses', 14, y);
        y += 5;

        doc.autoTable({
            startY: y,
            head: [['Date', 'Description', 'Category', 'Amount']],
            body: topExpenses.map(t => [
                (t.date || '').slice(0, 10),
                (t.description || '').slice(0, 40),
                t.category || '—',
                `₹${Math.abs(t.amount || 0).toLocaleString()}`,
            ]),
            theme: 'striped',
            headStyles: { fillColor: [168, 85, 247] },
            margin: { left: 14, right: 14 },
        });

        y = doc.lastAutoTable.finalY + 15;
    }

    // Goals Progress
    if (goals && goals.length > 0) {
        if (y > 240) { doc.addPage(); y = 20; }
        doc.setFontSize(14);
        doc.text('Goals Progress', 14, y);
        y += 5;

        doc.autoTable({
            startY: y,
            head: [['Goal', 'Target', 'Saved', 'Progress']],
            body: goals.map(g => [
                g.title,
                `₹${(g.price || 0).toLocaleString()}`,
                `₹${(g.saved || 0).toLocaleString()}`,
                `${g.price > 0 ? ((g.saved / g.price) * 100).toFixed(1) : 0}%`,
            ]),
            theme: 'striped',
            headStyles: { fillColor: [168, 85, 247] },
            margin: { left: 14, right: 14 },
        });
    }

    // Footer
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFontSize(8);
        doc.setTextColor(148, 163, 184);
        doc.text(`Page ${i} of ${pageCount} — Strategic Finance Portal`, pageWidth / 2, doc.internal.pageSize.getHeight() - 10, { align: 'center' });
    }

    doc.save(`finance-report-${month || 'current'}.pdf`);
}
